// utils/invoicing/courierXML.js
//
// Generates Finvoice 3.0 XML for frontend upload to Netvisor.
// Groups deliveries by sender yNumber → one Finvoice file per company.
// Returns { yNumber, companyName, xml }[]
//
// Optional: Pass custom seller info via options.seller parameter

const fmt = {
    // Finvoice date format: CCYYMMDD (for XML elements)
    date: (ms) => {
      const d = ms ? new Date(ms) : new Date();
      return d.toISOString().split('T')[0].replace(/-/g, '');
    },
    // Human-readable date format: DD.MM.YYYY (for text fields)
    fmtDate: (ms) => {
      if (!ms) return '—';
      const d = new Date(ms);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}.${month}.${year}`;
    },
    dueDate: (days = 14) => {
      const d = new Date();
      d.setDate(d.getDate() + days);
      return d.toISOString().split('T')[0].replace(/-/g, '');
    },
    amount: (n) => Number(n || 0).toFixed(2).replace('.', ','),
    sanitize: (str) => (str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'),
    address: (addr) =>
      (addr || '').split(',').slice(0, 2).map((s) => s.trim()).join(', '),
  };
  
  // Default seller details — MyXline
  const DEFAULT_SELLER = {
    name:    'Suomen Bussipaketit Oy',
    taxCode: 'FI32194356',       // FI + y-number digits without dash
    yNumber: '3219435-6',
    iban:    'FI7350620320353604',
    bic:     'OKOYFIHH',
  };
  
  function buildFinvoice(yNumber, deliveries, options = {}) {
    const { noBillingFee = false, due = 14, invoiceNumber, seller = DEFAULT_SELLER } = options;
  
    const first       = deliveries[0];
    const companyName = fmt.sanitize(first.billing?.companyName || yNumber);
    const buyerTaxCode = 'FI' + yNumber.replace('-', '');
    const invDate     = fmt.date();
    const dueDate     = fmt.dueDate(due);
    const invNumber   = invoiceNumber || `${Date.now()}`;
  
    // Build invoice rows
    const rows = deliveries
      .slice()
      .sort((a, b) => (b.targetTime || 0) - (a.targetTime || 0))
      .map((d, i) => {
        const from  = fmt.sanitize(fmt.address(d.originInfo?.address || d.origin));
        const to    = fmt.sanitize(fmt.address(d.destinationInfo?.address || d.destination));
        const date  = fmt.fmtDate(d.targetTime || d.completedAtMs);
        const id    = fmt.sanitize(d.deliveryId || d.id || '');
        const ref   = fmt.sanitize(d.billing?.paymentReference || '');
        const price = Number(d.price || 0);
        const net   = price / 1.255;  // brutto → net at 25.5%
        const vat   = price - net;
  
        let freeText = `Reitti: ${from} - ${to}, Tunnus: ${id}, Toimitusaika: ${date}`;
        if (ref) freeText += `, Viite: ${ref}`;
  
        return `    <InvoiceRow>
            <ArticleIdentifier>PH-1</ArticleIdentifier>
            <ArticleName>Tilaus</ArticleName>
            <DeliveredQuantity QuantityUnitCode="kpl">1,00</DeliveredQuantity>
            <UnitPriceAmount AmountCurrencyIdentifier="EUR">${fmt.amount(net)}</UnitPriceAmount>
            <RowVatRatePercent>25,50</RowVatRatePercent>
            <RowVatAmount AmountCurrencyIdentifier="EUR">${fmt.amount(vat)}</RowVatAmount>
            <RowAmount AmountCurrencyIdentifier="EUR">${fmt.amount(price)}</RowAmount>
            <RowFreeText>${fmt.sanitize(freeText)}</RowFreeText>
        </InvoiceRow>`;
      })
      .join('\n');
  
    // Billing fee row
    const feeNet   = 6.0;
    const feeVat   = feeNet * 0.255;
    const feeTotal = feeNet + feeVat;
    const feeRow = noBillingFee ? '' : `
        <InvoiceRow>
            <ArticleIdentifier>L-1</ArticleIdentifier>
            <ArticleName>Palvelumaksu</ArticleName>
            <DeliveredQuantity QuantityUnitCode="kpl">1,00</DeliveredQuantity>
            <UnitPriceAmount AmountCurrencyIdentifier="EUR">${fmt.amount(feeNet)}</UnitPriceAmount>
            <RowVatRatePercent>25,50</RowVatRatePercent>
            <RowVatAmount AmountCurrencyIdentifier="EUR">${fmt.amount(feeVat)}</RowVatAmount>
            <RowAmount AmountCurrencyIdentifier="EUR">${fmt.amount(feeTotal)}</RowAmount>
            <RowFreeText>Palvelumaksu</RowFreeText>
        </InvoiceRow>`;
  
    // Totals
    const totalNet   = deliveries.reduce((s, d) => s + Number(d.price || 0) / 1.255, 0)
                       + (noBillingFee ? 0 : feeNet);
    const totalVat   = deliveries.reduce((s, d) => s + Number(d.price || 0) - Number(d.price || 0) / 1.255, 0)
                       + (noBillingFee ? 0 : feeVat);
    const totalGross = totalNet + totalVat;
  
    return `<?xml version="1.0" encoding="UTF-8"?>
    <?xml-stylesheet type="text/xsl" href="Finvoice.xsl"?>
    <Finvoice>
        <SellerPartyDetails>
            <SellerPartyIdentifier>${seller.yNumber}</SellerPartyIdentifier>
            <SellerOrganisationName>${seller.name}</SellerOrganisationName>
            <SellerOrganisationTaxCode>${seller.taxCode}</SellerOrganisationTaxCode>
        </SellerPartyDetails>
        <SellerAccountDetails>
            <SellerAccountID IdentificationSchemeName="IBAN">${seller.iban}</SellerAccountID>
            <SellerBic IdentificationSchemeName="BIC">${seller.bic}</SellerBic>
        </SellerAccountDetails>
        <BuyerPartyDetails>
            <BuyerPartyIdentifier>${yNumber}</BuyerPartyIdentifier>
            <BuyerOrganisationName>${companyName}</BuyerOrganisationName>
            <BuyerOrganisationTaxCode>${buyerTaxCode}</BuyerOrganisationTaxCode>
        </BuyerPartyDetails>
        <InvoiceDetails>
            <InvoiceTypeCode>INV01</InvoiceTypeCode>
            <InvoiceNumber>${invNumber}</InvoiceNumber>
            <InvoiceDate Format="CCYYMMDD">${invDate}</InvoiceDate>
            <SellerReferenceIdentifier>${invNumber}</SellerReferenceIdentifier>
            <PaymentTermsDetails>
                <InvoiceDueDate Format="CCYYMMDD">${dueDate}</InvoiceDueDate>
            </PaymentTermsDetails>
            <InvoiceTotalVatExcludedAmount AmountCurrencyIdentifier="EUR">${fmt.amount(totalNet)}</InvoiceTotalVatExcludedAmount>
            <InvoiceTotalVatAmount AmountCurrencyIdentifier="EUR">${fmt.amount(totalVat)}</InvoiceTotalVatAmount>
            <InvoiceTotalVatIncludedAmount AmountCurrencyIdentifier="EUR">${fmt.amount(totalGross)}</InvoiceTotalVatIncludedAmount>
        </InvoiceDetails>
    ${rows}${feeRow}
        <EpiDetails>
            <EpiIdentificationDetails>
                <EpiDate Format="CCYYMMDD">${dueDate}</EpiDate>
            </EpiIdentificationDetails>
            <EpiPartyDetails>
                <EpiBfiPartyDetails>
                    <EpiBfiIdentifier IdentificationSchemeName="BIC">${seller.bic}</EpiBfiIdentifier>
                </EpiBfiPartyDetails>
                <EpiBeneficiaryPartyDetails>
                    <EpiNameAddressDetails>${seller.name}</EpiNameAddressDetails>
                    <EpiAccountID IdentificationSchemeName="IBAN">${seller.iban}</EpiAccountID>
                </EpiBeneficiaryPartyDetails>
            </EpiPartyDetails>
            <EpiPaymentInstructionDetails>
                <EpiPaymentMethod>42</EpiPaymentMethod>
                <EpiAmount AmountCurrencyIdentifier="EUR">${fmt.amount(totalGross)}</EpiAmount>
            </EpiPaymentInstructionDetails>
        </EpiDetails>
    </Finvoice>`;
  }
  
  /**
   * Group deliveries by sender yNumber, return one Finvoice XML per company.
   * 
   * @param {Object[]} deliveries - Array of delivery objects with billing info
   * @param {Object} options - Optional configuration
   * @param {Object} options.seller - Custom seller details (defaults to MyXline)
   *   @param {string} options.seller.name - Organization name
   *   @param {string} options.seller.yNumber - Finnish Y-number (e.g., '3219435-6')
   *   @param {string} options.seller.taxCode - Tax code with FI prefix (e.g., 'FI32194356')
   *   @param {string} options.seller.iban - IBAN account number
   *   @param {string} options.seller.bic - BIC code
   * @param {number} options.due - Payment term in days (default: 14)
   * @param {boolean} options.noBillingFee - Skip service fee row (default: false)
   * @returns {{ yNumber: string, companyName: string, xml: string }[]}
   * 
   * @example
   * // Use default seller (MyXline)
   * const xmls = makeFinvoiceXml(deliveries);
   * 
   * @example
   * // Use custom seller
   * const xmls = makeFinvoiceXml(deliveries, {
   *   seller: {
   *     name: 'Custom Company Oy',
   *     yNumber: '1234567-8',
   *     taxCode: 'FI12345678',
   *     iban: 'FI...',
   *     bic: 'XXXXXXXX',
   *   }
   * });
   */
  export function makeFinvoiceXml(deliveries, options = {}) {
    const { seller = DEFAULT_SELLER, ...buildOptions } = options;
    
    const groups = new Map();
    for (const d of deliveries) {
      const yNumber = d.billing?.yNumber?.trim();
      if (!yNumber) {
        console.warn(`Delivery ${d.deliveryId || d.id} has no billing.yNumber — skipped`);
        continue;
      }
      if (!groups.has(yNumber)) groups.set(yNumber, []);
      groups.get(yNumber).push(d);
    }
  
    return Array.from(groups.entries()).map(([yNumber, group]) => ({
      yNumber,
      companyName: group[0].billing?.companyName || yNumber,
      xml: buildFinvoice(yNumber, group, { seller, ...buildOptions }),
    }));
  }
  
  export { DEFAULT_SELLER };